import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    ThemeProvider,
    Tooltip,
    Typography,
    createTheme,
} from '@mui/material';
import MUIDataTable from 'mui-datatables';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../services/api';
import textLabels from '../../components/Datatable/textLabelsTable';

const ACTION_LABELS = {
    create: 'Criou',
    update: 'Editou',
    delete: 'Excluiu',
    deactivate: 'Desativou',
    reactivate: 'Reativou',
    bulk_create: 'Inseriu em massa',
    bulk_delete: 'Removeu em massa',
    sync: 'Sincronizou',
    validate: 'Validou',
};

const ACTION_COLORS = {
    create: 'success',
    update: 'info',
    delete: 'error',
    deactivate: 'warning',
    reactivate: 'success',
    bulk_create: 'success',
    bulk_delete: 'error',
    sync: 'info',
    validate: 'default',
};

const ENTITY_LABELS = {
    blacklist: 'Domínio',
    category: 'Categoria DNS',
    user: 'Usuário',
    role: 'Cargo',
    nas: 'NAS',
    radius_user: 'Usuário RADIUS',
    pegeia: 'Fonte Syslog',
    dns_node: 'Nó DNS',
    dns_localzone: 'Zona DNS',
    dns_acl: 'ACL DNS',
    dns_blacklist: 'Blacklist DNS',
    access: 'Acesso RADIUS',
    group: 'Grupo',
    audit: 'Auditoria',
};

const PAGE_THEME = createTheme({
    palette: { mode: 'light', primary: { main: '#e01b29' } },
    typography: { fontFamily: ['sans-serif', 'Roboto', 'Montserrat'] },
});

const fmtDate = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleString('pt-BR');
    } catch {
        return iso;
    }
};

export default function Audit() {
    useEffect(() => {
        document.title = 'IPV7 Tools - Auditoria';
    }, []);

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [order, setOrder] = useState({ field: 'created_at', dir: 'desc' });

    const [filters, setFilters] = useState({
        query: '',
        entity: '',
        action: '',
        date_from: '',
        date_to: '',
    });

    const [detail, setDetail] = useState({ open: false, row: null });

    const fetchData = async () => {
        setLoading(true);
        try {
            const body = {
                limit: rowsPerPage,
                offset: page * rowsPerPage,
                order_by: order.field,
                order: order.dir,
                query: filters.query || null,
                entity: filters.entity || null,
                action: filters.action || null,
                date_from: filters.date_from || null,
                date_to: filters.date_to || null,
            };
            const res = await api.post('/audit/', body);
            setRows(res.data?.data || []);
            setTotal(res.data?.total || 0);
        } catch (e) {
            console.error(e);
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage, order, filters]);

    const handleFilterChange = (k) => (e) => {
        setFilters((prev) => ({ ...prev, [k]: e.target.value }));
        setPage(0);
    };

    const clearFilters = () => {
        setFilters({ query: '', entity: '', action: '', date_from: '', date_to: '' });
        setPage(0);
    };

    const columns = useMemo(
        () => [
            {
                name: 'created_at',
                label: 'Data',
                options: {
                    sort: true,
                    customBodyRender: (v) => fmtDate(v),
                },
            },
            {
                name: 'username',
                label: 'Usuário',
                options: {
                    sort: false,
                    customBodyRender: (v) => v || <em style={{ color: '#888' }}>—</em>,
                },
            },
            {
                name: 'action',
                label: 'Ação',
                options: {
                    sort: false,
                    customBodyRender: (v) => (
                        <Chip
                            size="small"
                            label={ACTION_LABELS[v] || v}
                            color={ACTION_COLORS[v] || 'default'}
                            variant="outlined"
                        />
                    ),
                },
            },
            {
                name: 'entity',
                label: 'Entidade',
                options: {
                    sort: false,
                    customBodyRender: (v) => ENTITY_LABELS[v] || v,
                },
            },
            {
                name: 'entity_name',
                label: 'Identificador',
                options: {
                    sort: false,
                    customBodyRender: (v) => v || <em style={{ color: '#888' }}>—</em>,
                },
            },
            {
                name: 'summary',
                label: 'Resumo',
                options: { sort: false },
            },
            {
                name: 'ip',
                label: 'IP',
                options: { sort: false },
            },
            {
                name: 'actions',
                label: ' ',
                options: {
                    filter: false,
                    sort: false,
                    customBodyRenderLite: (dataIndex) => (
                        <Tooltip title="Ver payload completo">
                            <IconButton
                                size="small"
                                onClick={() => setDetail({ open: true, row: rows[dataIndex] })}
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ),
                },
            },
        ],
        [rows],
    );

    const options = {
        textLabels: {
            ...textLabels,
            body: {
                noMatch: loading ? <CircularProgress color="info" /> : 'Nenhum registro encontrado',
            },
        },
        count: total,
        page,
        rowsPerPage,
        rowsPerPageOptions: [10, 20, 50, 100, 200],
        serverSide: true,
        selectableRows: 'none',
        viewColumns: false,
        filter: false,
        search: false,
        print: false,
        download: false,
        responsive: 'standard',
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    setPage(tableState.page);
                    break;
                case 'changeRowsPerPage':
                    setRowsPerPage(tableState.rowsPerPage);
                    setPage(0);
                    break;
                case 'sort':
                    if (tableState.sortOrder?.name === 'created_at') {
                        setOrder({ field: 'created_at', dir: tableState.sortOrder.direction });
                    }
                    break;
                default:
                    break;
            }
        },
    };

    return (
        <ThemeProvider theme={PAGE_THEME}>
            <Box sx={{ p: 2 }}>
                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Buscar (usuário, identificador, resumo)"
                                value={filters.query}
                                onChange={handleFilterChange('query')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Entidade</InputLabel>
                                <Select
                                    label="Entidade"
                                    value={filters.entity}
                                    onChange={handleFilterChange('entity')}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    {Object.entries(ENTITY_LABELS).map(([k, v]) => (
                                        <MenuItem key={k} value={k}>
                                            {v}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Ação</InputLabel>
                                <Select
                                    label="Ação"
                                    value={filters.action}
                                    onChange={handleFilterChange('action')}
                                >
                                    <MenuItem value="">Todas</MenuItem>
                                    {Object.entries(ACTION_LABELS).map(([k, v]) => (
                                        <MenuItem key={k} value={k}>
                                            {v}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="datetime-local"
                                label="De"
                                InputLabelProps={{ shrink: true }}
                                value={filters.date_from}
                                onChange={handleFilterChange('date_from')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="datetime-local"
                                label="Até"
                                InputLabelProps={{ shrink: true }}
                                value={filters.date_to}
                                onChange={handleFilterChange('date_to')}
                            />
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <Stack direction="row" spacing={1}>
                                <Tooltip title="Atualizar">
                                    <IconButton onClick={fetchData} size="small">
                                        <RefreshIcon />
                                    </IconButton>
                                </Tooltip>
                                <Button size="small" onClick={clearFilters}>
                                    Limpar
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                <MUIDataTable
                    title={`Auditoria — ${total} registro(s)`}
                    data={rows}
                    columns={columns}
                    options={options}
                />

                <DetailDialog open={detail.open} row={detail.row} onClose={() => setDetail({ open: false, row: null })} />
            </Box>
        </ThemeProvider>
    );
}

function DetailDialog({ open, row, onClose }) {
    if (!row) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Detalhe da auditoria</DialogTitle>
                <DialogContent>—</DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Fechar</Button>
                </DialogActions>
            </Dialog>
        );
    }

    const payload = row.payload && typeof row.payload === 'object'
        ? row.payload
        : (typeof row.payload === 'string' ? safeParse(row.payload) : null);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {ACTION_LABELS[row.action] || row.action}
                {' — '}
                {ENTITY_LABELS[row.entity] || row.entity}
                {row.entity_name ? ` "${row.entity_name}"` : ''}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    <Field label="Data" value={fmtDate(row.created_at)} />
                    <Field label="Usuário" value={row.username || '—'} />
                    <Field label="IP" value={row.ip || '—'} />
                    <Field label="Resumo" value={row.summary || '—'} />
                    <Field label="ID da entidade" value={row.entity_id ?? '—'} />
                </Stack>

                {payload && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Payload
                        </Typography>
                        <Box
                            component="pre"
                            sx={{
                                bgcolor: '#f5f5f5',
                                p: 1.5,
                                borderRadius: 1,
                                fontSize: 12,
                                overflow: 'auto',
                                maxHeight: 400,
                            }}
                        >
                            {JSON.stringify(payload, null, 2)}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}

function Field({ label, value }) {
    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Typography variant="caption" sx={{ width: 130, color: 'text.secondary', pt: 0.3 }}>
                {label}
            </Typography>
            <Typography variant="body2">{value}</Typography>
        </Box>
    );
}

function safeParse(s) {
    try {
        return JSON.parse(s);
    } catch {
        return s;
    }
}
